// Route stub created by tools/gen_web_routes.py from contract/catalog/screens.json; owned by hand from here on (A4U7):
// the filters live in the URL, and the loader warms the list through the same queryOptions the screen reads — prefetched,
// never awaited to a throw, so a failed list errors only its part (D193).
import { ids } from '@orca/api-client/testIds'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { buttonVariants } from '../../../../components/ui/button'
import { LockedAction } from '../../../../components/ui/locked-action'
import { HeaderLock, PageHeader } from '../../../../components/ui/page-header'
import { SegmentedControl } from '../../../../components/ui/segmented-control'
import { Select } from '../../../../components/ui/select'
import { TextField } from '../../../../components/ui/text-field'
import { UsageMeter } from '../../../../components/ui/usage-meter'
import { MembersList } from '../../../../features/members/MembersList'
import { PeopleTabs } from '../../../../features/members/PeopleTabs'
import { membersQuery, planQuery } from '../../../../features/members/queries'
import { teamsQuery } from '../../../../features/teams/queries'
import '../../../../features/teams/strings'
import { useCan, useCanSomewhere } from '../../../../shared/access/access'
import { text } from '../../../../shared/routing/search'
import { Part } from '../../../../shared/shell/StatePart'
import { useWindowClass } from '../../../../shared/shell/useWindowClass'

const ROLES = ['owner', 'admin', 'manager', 'member', 'viewer'] as const
const STATUSES = ['', 'active', 'suspended'] as const

export const Route = createFileRoute('/o/$orgSlug/people/')({
  staticData: { screenId: 'MembersListScreen', access: 'member.read' },
  // The route key's query arguments (screens.json `args`), as the apps' LinkResolver reads them;
  // unknown keys are dropped.
  validateSearch: (
    search: Record<string, unknown>,
  ): { role?: string; teamId?: string; status?: string; q?: string; member?: string } => ({
    role: text(search.role),
    teamId: text(search.teamId),
    status: text(search.status),
    q: text(search.q),
    member: text(search.member),
  }),
  loaderDeps: ({ search: { role, teamId, status, q } }) => ({ role, teamId, status, q }),
  loader: async ({ context: { queryClient, membership }, deps }) => {
    await queryClient.prefetchQuery(membersQuery(membership.orgId, deps))
    return { orgId: membership.orgId, orgName: membership.name }
  },
  component: MembersListScreen,
})

function MembersListScreen() {
  const { t } = useTranslation()
  const { orgId, orgName } = Route.useLoaderData()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filters = { role: search.role, teamId: search.teamId, status: search.status, q: search.q }
  const canInvite = useCanSomewhere(orgId, 'member.invite')
  const canReadTeams = useCan(orgId, 'team.read')
  const canReadPlan = useCan(orgId, 'plan.read')
  const { orgSlug } = Route.useParams()
  const { data: teams } = useQuery({ ...teamsQuery(orgId), enabled: canReadTeams })
  const { data: plan } = useQuery({ ...planQuery(orgId), enabled: canReadPlan })
  const seats = plan?.features.find((f) => f.key === 'members.max')
  const phone = useWindowClass() === 'compact'
  return (
    <main data-testid={ids.membersList.root} className="flex flex-col gap-(--stack-lg)">
      <PageHeader
        title={t('members.people.title')}
        actions={
          canInvite ? (
            <Link
              to="/o/$orgSlug/people/invite"
              params={{ orgSlug }}
              data-testid={ids.membersList.inviteButton}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {t('members.list.invite')}
            </Link>
          ) : phone ? null : (
            <HeaderLock
              label={t('members.list.invite')}
              reason={t('members.locked.invite', { org: orgName })}
              testId={ids.membersList.inviteButton}
            />
          )
        }
      >
        {/* Below 600 px the lock keeps its reason under it (D193), under the row; from 600 it sits in the row. */}
        {!canInvite && phone && (
          <LockedAction
            label={t('members.list.invite')}
            reason={t('members.locked.invite', { org: orgName })}
            testId={ids.membersList.inviteButton}
            className="w-full max-w-sm"
          />
        )}
      </PageHeader>
      <PeopleTabs orgId={orgId} orgSlug={orgSlug} current="members" />
      {seats?.limit != null && (
        <UsageMeter
          used={seats.used ?? 0}
          limit={seats.limit}
          nearly={0.8}
          testId={ids.membersList.seatsValue}
          className="max-w-sm"
        />
      )}
      <div className="flex flex-wrap gap-(--inline-md)">
        <TextField
          label={t('members.list.search')}
          data-testid={ids.membersList.searchField}
          type="search"
          defaultValue={search.q ?? ''}
          onChange={(event) =>
            void navigate({ search: (s) => ({ ...s, q: event.target.value || undefined }), replace: true })
          }
        />
        <Select
          label={t('members.list.role')}
          testId={ids.membersList.roleFilterField}
          options={['', ...ROLES]}
          selected={search.role ?? ''}
          optionKey={(role) => role}
          optionLabel={(role) => (role === '' ? t('members.list.role.all') : t(`members.role.${role}`))}
          onSelect={(role) => void navigate({ search: (s) => ({ ...s, role: role || undefined }) })}
        />
        {teams && teams.items.length > 0 && (
          <Select
            label={t('members.list.team')}
            testId={ids.membersList.teamFilterField}
            options={['', ...teams.items.map((team) => team.id)]}
            selected={search.teamId ?? ''}
            optionKey={(id) => id}
            optionLabel={(id) =>
              id === '' ? t('members.list.team.all') : (teams.items.find((team) => team.id === id)?.name ?? id)
            }
            onSelect={(id) => void navigate({ search: (s) => ({ ...s, teamId: id || undefined }) })}
          />
        )}
      </div>
      <SegmentedControl
        label={t('members.table.status')}
        testId={ids.membersList.statusFilterGroup}
        value={search.status ?? ''}
        options={STATUSES.map((status) => ({
          value: status,
          label: status === '' ? t('members.status.all') : t(`members.status.${status}`),
        }))}
        onChange={(status) => void navigate({ search: (s) => ({ ...s, status: status || undefined }) })}
        className="self-start"
      />
      <Part testId={ids.membersList.skeleton}>
        <MembersList
          orgId={orgId}
          orgSlug={orgSlug}
          filters={filters}
          openId={search.member}
          onOpen={(member) => void navigate({ search: (s) => ({ ...s, member }), replace: true })}
          canInvite={canInvite}
        />
      </Part>
    </main>
  )
}
